<template>
  <div class="pp-stats">
    <!-- Collected — the number the office matches against their deposit. -->
    <div class="pp-stat">
      <div class="pp-stat-label">Collected</div>
      <div class="pp-stat-figure">{{ loading ? '—' : money(stats.total) }}</div>
      <div class="pp-stat-sub">
        {{ stats.count }} payment{{ stats.count === 1 ? '' : 's' }} · {{ rangeLabel }}
      </div>
    </div>

    <!-- Synced to OD -->
    <div class="pp-stat">
      <div class="pp-stat-label">Synced to OD</div>
      <div class="pp-stat-row">
        <div class="pp-stat-figure pp-stat-figure--brand">{{ loading ? '—' : stats.syncedPct + '%' }}</div>
        <div class="pp-stat-of">{{ stats.synced }} of {{ stats.count }}</div>
      </div>
      <div class="pp-bar"><div class="pp-bar-fill" :style="{ width: stats.syncedPct + '%' }"></div></div>
      <!-- Explains the gap between `synced` and `count` when it is just our
           confirmation cadence. Without this the bar reads as money missing. -->
      <div v-if="stats.confirmingCount" class="pp-stat-sub">
        {{ stats.confirmingCount }} still confirming
      </div>
    </div>

    <!-- Needs attention -->
    <div class="pp-stat">
      <div class="pp-stat-label">Needs attention</div>
      <div class="pp-stat-row">
        <div class="pp-stat-figure" :class="stats.attnCount ? 'pp-stat-figure--red' : 'pp-stat-figure--dim'">
          {{ loading ? '—' : stats.attnCount }}
        </div>
        <i
          class="fa fa-exclamation-triangle pp-attn-icon"
          :class="{ 'pp-attn-icon--dim': !stats.attnCount }"
          aria-hidden="true"
        ></i>
      </div>
      <div class="pp-stat-sub">{{ money(stats.attnTotal) }} not in Open Dental</div>
    </div>

    <!-- Where it came from -->
    <div class="pp-stat">
      <div class="pp-stat-label">Where it came from</div>
      <div v-if="channelMix.length" class="pp-mix">
        <div v-for="c in channelMix" :key="c.key" class="pp-mix-row">
          <span class="pp-mix-dot" :style="{ background: c.color }"></span>
          <span class="pp-mix-label">{{ c.label }}</span>
          <span class="pp-mix-amount">{{ money(c.amount) }}</span>
        </div>
      </div>
      <div v-else class="pp-stat-sub pp-mix-empty">No payments in this range</div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'PaymentStatCards',
  props: {
    // { total, count, synced, syncedPct, attnCount, attnTotal }
    stats: { type: Object, required: true },
    // [{ key, label, color, amount }]
    channelMix: { type: Array, default: () => [] },
    rangeLabel: { type: String, default: '' },
    loading: { type: Boolean, default: false },
  },
  methods: {
    money(n) {
      const v = Number(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-stats {
  display: grid;
  grid-template-columns: 1.25fr 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
}

.pp-stat {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 18px 20px;
}

.pp-stat-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $pp-text-muted;
}

.pp-stat-figure {
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 38px;
  line-height: 1;
  letter-spacing: -0.03em;
  // Tabular figures so the numbers line up column-to-column and don't jitter
  // as the range changes — this is a reconciliation surface.
  font-variant-numeric: tabular-nums;
  color: $pp-text;
  margin-top: 12px;

  &--brand { color: $pp-brand; }
  &--red { color: $pp-red-text; }
  &--dim { color: $pp-border-strong; }
}

.pp-stat-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.pp-stat-of {
  font-size: 13px;
  color: $pp-text-muted;
  font-variant-numeric: tabular-nums;
}

.pp-stat-sub {
  font-size: 12.5px;
  color: $pp-text-muted;
  margin-top: 8px;
}

.pp-attn-icon {
  font-size: 20px;
  color: $pp-red;
  align-self: center;
  margin-top: 12px;

  &--dim { color: $pp-border-strong; }
}

.pp-bar {
  height: 6px;
  border-radius: 6px;
  background: $pp-brand-subtle;
  margin-top: 14px;
  overflow: hidden;
}

.pp-bar-fill {
  height: 100%;
  background: $pp-brand;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.pp-mix {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 14px;
}

.pp-mix-row {
  display: flex;
  align-items: center;
  gap: 9px;
}

.pp-mix-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.pp-mix-label {
  font-size: 13px;
  font-weight: 600;
  color: $pp-text-strong;
}

.pp-mix-amount {
  margin-left: auto;
  font-size: 13px;
  color: $pp-text-muted;
  font-variant-numeric: tabular-nums;
}

.pp-mix-empty { margin-top: 14px; }
</style>
